// Compact responsive roster with focused member panels. The existing bounded member
// endpoints and editor-owned drafts/tokens are unchanged. Identity edits travel to the
// shared person record; demographics stay scoped to this application.
import { useEffect, useId, useRef, useState } from "react"
import { skipToken } from "@reduxjs/toolkit/query"
import { benefitApi } from "@/features/benefit-workspace/benefitApi"
import { useBenefitScopeArg } from "@/features/benefit-workspace/useBenefitChildReads"
import { useBenefitDraftState, useBenefitReviewActive, useBenefitSaving } from "@/features/benefit-workspace/benefitEditorContext"
import {
  IconHistory,
  IconLoader2,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { ButtonGroup } from "@/components/ui/button-group"
import { TooltipProvider } from "@/components/ui/tooltip"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import { RecordAction } from "./record-action"
import { useMemberRemoval } from "./use-member-removal"
import { useHasOpenBenefitEditors } from "./benefit-edit-scope"
import { demographicsRoster } from "@/lib/demographics-summary"
import "./demographics-details.css"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from "sonner"
import {
  PersonIdentityFields,
  PotentialDuplicates,
  usePotentialDuplicates,
} from "@/components/people/person-identity-form"
import { api } from "@/lib/api"
import {
  educationLabels,
  eligibilityLabels,
  ethnicityLabels,
  genderLabels,
  incomeSourceLabels,
  medicalInsuranceLabels,
  relationshipLabels,
  selectOptions,
  workStatusLabels,
} from "@/lib/benefit-enums"
import { enumLabel, fmtDate } from "@/lib/benefit-format"
import { saveBenefitChange } from "@/lib/benefit-save"
import { householdMemberSummary } from "@/lib/household-member-summary"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"
import { useAppSelector } from "@/store"
import type { BenefitDetailDto, PersonDto } from "@/lib/benefit-types"
import {
  completedYears,
  emptyHouseholdMemberDraft,
  hasDisabilityFlag,
  householdMemberDemographics,
  householdMemberDraft,
  householdMemberRequest,
  setDisabilityFlag,
  type HouseholdMemberDraft,
} from "@/lib/household"
import {
  displaySsn,
  emptyPersonIdentity,
  formatPersonName,
  maskSsn,
  newHouseholdMemberPerson,
  validatePersonIdentity,
} from "@/lib/person-identity"

import type { StagedHouseholdPerson } from "@/features/benefit-workspace/benefitEditorDrafts"
export type { StagedHouseholdPerson } from "@/features/benefit-workspace/benefitEditorDrafts"

const genderSelectOptions = selectOptions(genderLabels)
const ethnicityOptions = selectOptions(ethnicityLabels)
const workStatusOptions = selectOptions(workStatusLabels)
const educationOptions = selectOptions(educationLabels)
const medicalInsuranceOptions = selectOptions(medicalInsuranceLabels)
const eligibilityOptions = selectOptions(eligibilityLabels)

// Self identifies the applicant; the server refuses it for anyone else, so it is not offered.
const relationshipOptions = selectOptions(relationshipLabels).filter(
  (option) => option.value !== "0",
)

function memberName(person: { lastName: string; firstName: string; middleName: string }): string {
  return formatPersonName(person)
}

function LabeledSelect({
  id,
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  id: string
  label: string
  value: string
  options: { value: string; label: string }[]
  disabled?: boolean
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} disabled={disabled} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full"><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

/**
 * The demographics captured about a member on this application. Shared by the member card
 * and the add-member dialog so both write exactly the same shape.
 */
export function MemberDemographicFields({
  draft,
  onChange,
  disabled,
  isApplicant,
  idPrefix,
}: {
  draft: HouseholdMemberDraft
  onChange: (next: HouseholdMemberDraft) => void
  disabled: boolean
  isApplicant: boolean
  idPrefix: string
}) {
  const set = (patch: Partial<HouseholdMemberDraft>) => onChange({ ...draft, ...patch })
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {isApplicant ? (
        <div className="space-y-1.5">
          <Label>Relationship</Label>
          <p className="pt-2 text-sm">Self — the applicant on this application</p>
        </div>
      ) : (
        <LabeledSelect
          id={`${idPrefix}-relationship`}
          label="Relationship to Applicant"
          value={draft.relationship}
          options={relationshipOptions}
          disabled={disabled}
          onChange={(relationship) => set({ relationship })}
        />
      )}
      <LabeledSelect
        id={`${idPrefix}-ethnicity`}
        label="Ethnicity"
        value={draft.ethnicity}
        options={ethnicityOptions}
        disabled={disabled}
        onChange={(ethnicity) => set({ ethnicity })}
      />
      <LabeledSelect
        id={`${idPrefix}-work-status`}
        label="Work Status"
        value={draft.workStatus}
        options={workStatusOptions}
        disabled={disabled}
        onChange={(workStatus) => set({ workStatus })}
      />
      <LabeledSelect
        id={`${idPrefix}-education`}
        label="Education Level"
        value={draft.educationLevel}
        options={educationOptions}
        disabled={disabled}
        onChange={(educationLevel) => set({ educationLevel })}
      />
      <LabeledSelect
        id={`${idPrefix}-medical-insurance`}
        label="Medical Insurance"
        value={draft.medicalInsurance}
        options={medicalInsuranceOptions}
        disabled={disabled}
        onChange={(medicalInsurance) => set({ medicalInsurance })}
      />
      <LabeledSelect
        id={`${idPrefix}-eligibility`}
        label="LIHEAP Eligibility"
        value={draft.eligibilityStatus}
        options={eligibilityOptions}
        disabled={disabled}
        onChange={(eligibilityStatus) => set({ eligibilityStatus })}
      />
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-characteristics`}>Other Characteristics</Label>
        <Input
          id={`${idPrefix}-characteristics`}
          maxLength={50}
          value={draft.otherCharacteristics}
          disabled={disabled}
          onChange={(event) => set({ otherCharacteristics: event.target.value })}
        />
      </div>
      <label className="flex items-center gap-2 text-sm sm:col-span-2">
        <Checkbox
          checked={hasDisabilityFlag(draft.otherCharacteristics)}
          disabled={disabled}
          onCheckedChange={(checked) =>
            set({ otherCharacteristics: setDisabilityFlag(draft.otherCharacteristics, checked === true) })
          }
        />
        Disabled — counts toward the household's derived disabled total
      </label>
    </div>
  )
}

function MemberRecord({
  benefit,
  person,
  canEdit,
  onUpdated,
  onRemove,
  removing,
  showRow = true,
}: {
  showRow?: boolean
  benefit: BenefitDetailDto
  person: PersonDto
  canEdit: boolean
  onUpdated: (updated: BenefitDetailDto) => void
  onRemove(personId: string, opener: HTMLElement): void
  removing: boolean
}) {
  const [viewing, setViewing] = useState(false)
  const titleId = useId()
  const trigger = useRef<HTMLButtonElement | null>(null)
  const row = useRef<HTMLElement | null>(null)
  const restoreRowFocus = () => {
    // A new token temporarily removes edit actions while server capabilities refresh.
    // Return to the row's View action if the original button no longer exists.
    const target = trigger.current?.isConnected ? trigger.current : row.current?.querySelector<HTMLButtonElement>("button")
      ?? document.querySelector<HTMLElement>('[data-member-heading]')
    target?.focus()
  }
  const anyEditing = useHasOpenBenefitEditors()
  const canViewIdentifier = useAppSelector(state => state.permissions.permissions.includes("people:full-identifiers"))
  const [isEditing, setIsEditing, editToken] = useBenefitEdit(benefit.concurrencyToken, `member:${person.id}`)
  const [isSaving, setIsSaving] = useBenefitSaving()
  const editButton = useRef<HTMLButtonElement | null>(null)
  const wasEditing = useRef(false)
  useEffect(() => {
    if (wasEditing.current && !isEditing) editButton.current?.focus({ preventScroll: true })
    wasEditing.current = isEditing
  }, [isEditing])
  const reviewing = useBenefitReviewActive()
  const suspended = removing || reviewing
  const suspendedRef = useRef(suspended); suspendedRef.current = suspended
  const [draft, setDraft] = useBenefitDraftState(`member:${person.id}`, "draft", () => householdMemberDraft(person))
  const [context, setContext] = useBenefitDraftState(`member:${person.id}`, "context", null)
  const isApplicant = person.id === (isEditing ? context?.applicantId : benefit.applicantId)
  const targetCurrent = benefit.personCollection.filter(member => member.id === person.id).length === 1
    && (!isEditing || context?.applicantId === benefit.applicantId)
  const canSave = canEdit && targetCurrent

  useEffect(() => {
    if (!isEditing) setDraft(householdMemberDraft(person))
  }, [person, isEditing])

  const save = async () => {
    if (!isEditing || isSaving || suspended || !canSave) return
    const { error, request } = householdMemberRequest(draft, editToken)
    if (error || !request) {
      toast.error(error ?? "The household member could not be saved.")
      return
    }

    setIsSaving(true)
    const { saved, reloaded } = await saveBenefitChange(
      benefit.id,
      onUpdated,
      () => api.patch<BenefitDetailDto>(
        `/api/benefitapplications/${benefit.id}/household-members/${person.id}`,
        request,
      ),
      "Household member updated.",
    )
    setIsSaving(false)
    if (saved || reloaded) setIsEditing(false)
  }

  const cancel = () => setIsEditing(false)

  const age = completedYears(person.birthDate, benefit.applicationDate)
  const name = memberName(isEditing && context ? context.person : person)
  const summary = householdMemberSummary(person)
  const recordedIncomeSource = person.sourceOfIncome == null || person.sourceOfIncome === 255
    ? "Not answered" : enumLabel(incomeSourceLabels, person.sourceOfIncome)
  const rosterFacts = [
    { label: "Date of birth", value: fmtDate(person.birthDate) },
    { label: "Age at application", value: age === null ? "Unavailable" : String(age) },
    { label: "Gender", value: enumLabel(genderLabels, person.gender) },
    { label: "SSN", value: canViewIdentifier ? displaySsn(person.socialSecurityNumber) : maskSsn(person.socialSecurityNumber) },
    ...summary.filter(field => ["Work Status", "Education"].includes(field.label)),
    { label: "Recorded income source", value: recordedIncomeSource },
    ...summary.filter(field => !["Work Status", "Education"].includes(field.label)),
  ]
  const startEdit = (button: HTMLButtonElement) => {
    if (!canSave) return
    setContext({ person: structuredClone(person), applicantId: benefit.applicantId })
    setDraft(householdMemberDraft(person))
    if (!setIsEditing(true)) return
    if (!viewing) trigger.current = button
  }
  const startRemove = (button: HTMLButtonElement) => onRemove(person.id, button)
  const close = () => {
    if (isEditing || isSaving || removing) { toast.error("Save or cancel the open editor before closing this panel."); return }
    setViewing(false)
  }

  return (
    <>
      {showRow && <article ref={row} data-member-row={person.id} className="demographics-member-record" aria-labelledby={titleId}>
        <header className="demographics-member-header">
          <div className="demographics-member">
            <span className="demographics-avatar" aria-hidden="true">{[person.firstName, person.lastName].map(part => part?.trim()[0] ?? "").join("") || "?"}</span>
            <div>
              <h4 id={titleId}>{name}</h4>
              <div className="demographics-relationship"><span className="sr-only">Relationship to applicant: </span>{isApplicant ? <Badge variant="outline">Applicant (Self)</Badge> : enumLabel(relationshipLabels, person.relationship)}</div>
            </div>
          </div>
          <TooltipProvider><ButtonGroup className="record-actions" aria-label={`${name} actions`}>
            <RecordAction kind="view" label={`View member ${name}`} onClick={event => {
              if (anyEditing) { toast.error("Save or cancel the open benefit editor before opening another panel."); return }
              trigger.current = event.currentTarget; setViewing(true)
            }} />
            {canEdit && targetCurrent && <RecordAction kind="edit" label={`Edit member ${name}`} onClick={event => startEdit(event.currentTarget)} />}
            {canEdit && !isApplicant && <RecordAction kind="remove" label={`Remove ${name} from this application`} onClick={event => startRemove(event.currentTarget)} />}
          </ButtonGroup></TooltipProvider>
        </header>
        <dl className="demographics-roster-facts" aria-label={`Facts for ${name}`}>
          {rosterFacts.map(field => <div key={field.label}><dt>{field.label}</dt><dd className={field.label === "SSN" ? "font-mono" : undefined}>{field.value}</dd></div>)}
        </dl>
      </article>}

      {!suspended && <Sheet open={viewing || isEditing} onOpenChange={open => { if (!open) close() }}>
        <SheetContent data-member-panel={person.id} className="demographics-member-sheet" onCloseAutoFocus={event => { event.preventDefault(); if (!suspendedRef.current) restoreRowFocus() }}>
          <SheetHeader>
            <p className="demographics-eyebrow">Household member</p>
            <SheetTitle>{isEditing ? "Edit " : ""}{name}</SheetTitle>
            <SheetDescription>Identity fields are shared across applications; demographics are recorded on this application.</SheetDescription>
          </SheetHeader>
          <div className="demographics-panel-body" role="region" aria-label="Member details" tabIndex={0}>
          {isEditing ? <>
          {!targetCurrent && <p role="status">This member or applicant changed, disappeared or became ambiguous. Your original draft is retained; cancel and reopen against the current roster.</p>}
          <h3>Shared person identity</h3><p className="demographics-inline-note">Changes to these fields affect this person across applications. Protected identifiers remain unchanged.</p>
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-1.5"><Label htmlFor={`${person.id}-last`}>Last Name</Label><Input id={`${person.id}-last`} value={draft.lastName} disabled={isSaving} onChange={(event) => setDraft((d) => ({ ...d, lastName: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label htmlFor={`${person.id}-first`}>First Name</Label><Input id={`${person.id}-first`} value={draft.firstName} disabled={isSaving} onChange={(event) => setDraft((d) => ({ ...d, firstName: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label htmlFor={`${person.id}-middle`}>Middle Initial</Label><Input id={`${person.id}-middle`} value={draft.middleName} disabled={isSaving} onChange={(event) => setDraft((d) => ({ ...d, middleName: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label htmlFor={`${person.id}-ssn`}>Social Security Number</Label><Input id={`${person.id}-ssn`} placeholder={canViewIdentifier ? "123-45-6789" : "Identifier protected; unchanged on save"} value={draft.socialSecurityNumber} disabled={isSaving || !canViewIdentifier} onChange={(event) => setDraft((d) => ({ ...d, socialSecurityNumber: event.target.value }))} /></div>
            <div className="space-y-1.5"><Label htmlFor={`${person.id}-dob`}>Date of Birth</Label><Input id={`${person.id}-dob`} type="date" value={draft.birthDate} disabled={isSaving} onChange={(event) => setDraft((d) => ({ ...d, birthDate: event.target.value }))} /></div>
            <LabeledSelect id={`${person.id}-gender`} label="Gender" value={draft.gender} options={genderSelectOptions} disabled={isSaving} onChange={(gender) => setDraft((d) => ({ ...d, gender }))} />
          </div>
          <Separator />
          <h3>On this application</h3><p className="demographics-inline-note">Relationships and these demographic answers stay on this application. Household totals are derived by the server.</p>
          <MemberDemographicFields draft={draft} onChange={setDraft} disabled={isSaving} isApplicant={isApplicant} idPrefix={person.id} />
          </> : <Tabs defaultValue="demographics">
            <TabsList className="w-full"><TabsTrigger value="demographics">On this application</TabsTrigger><TabsTrigger value="identity">Identity</TabsTrigger></TabsList>
            <TabsContent value="demographics"><dl className="demographics-facts">
              <div><dt>Relationship</dt><dd>{isApplicant ? "Applicant (Self)" : enumLabel(relationshipLabels, person.relationship)}</dd></div>
              {summary.map(field => <div key={field.label}><dt>{field.label}</dt><dd>{field.value}</dd></div>)}
              <div><dt>Recorded income source</dt><dd>{recordedIncomeSource}</dd></div>
            </dl><p className="demographics-inline-note">These answers belong to this application. Not indicated does not mean a confirmed No.</p></TabsContent>
            <TabsContent value="identity"><dl className="demographics-facts">
              {[ ["Name", name], ["Date of birth", fmtDate(person.birthDate)], ["Age at application", age === null ? "Unavailable" : String(age)], ["Gender", enumLabel(genderLabels, person.gender)], ["SSN", canViewIdentifier ? displaySsn(person.socialSecurityNumber) : maskSsn(person.socialSecurityNumber)] ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
            </dl><p className="demographics-inline-note">This identity is shared across applications. Changing who applied is a separate applicant/household correction.</p></TabsContent>
          </Tabs>}
          </div>
          <SheetFooter><div className="demographics-panel-actions">
            {isEditing ? <>
              <Button variant="outline" onClick={cancel} disabled={isSaving}>Cancel</Button>
              <Button onClick={save} disabled={isSaving || !canSave}>{isSaving && <IconLoader2 className="size-4 animate-spin" />} Save member</Button>
            </> : <>
              {canEdit && !isApplicant && <Button data-member-remove variant="outline" onClick={event => startRemove(event.currentTarget)}><IconTrash className="size-4" />Remove member</Button>}
              {canEdit && <Button ref={editButton} variant="outline" onClick={event => startEdit(event.currentTarget)}>Edit member</Button>}
              <Button variant="outline" onClick={close}>Done</Button>
            </>}
          </div></SheetFooter>
        </SheetContent>
      </Sheet>}
    </>
  )
}

export function AddMemberDialog({
  benefit,
  concurrencyToken,
  open,
  onOpenChange,
  onUpdated,
  onStage,
  draftGroup = "add-member",
  canEdit = true,
  onReturnFocus,
}: {
  canEdit?: boolean
  onReturnFocus?: () => void
  benefit: BenefitDetailDto
  concurrencyToken: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdated: (updated: BenefitDetailDto) => void
  onStage?: (person: StagedHouseholdPerson) => void
  draftGroup?: "add-member" | "applicant-correction"
}) {
  const canViewIdentifier = useAppSelector(state => state.permissions.permissions.includes("people:full-identifiers"))
  const [mode, setMode] = useBenefitDraftState(draftGroup, "picker.mode", "existing")
  const [searchText, setSearchText] = useBenefitDraftState(draftGroup, "picker.searchText", "")
  const [submittedSearch, setSubmittedSearch] = useBenefitDraftState(draftGroup, "picker.submittedSearch", "")
  const [selected, setSelected] = useBenefitDraftState(draftGroup, "picker.selected", null)
  const [identity, setIdentity] = useBenefitDraftState(draftGroup, "picker.identity", emptyPersonIdentity)
  const [demographics, setDemographics] = useBenefitDraftState(draftGroup, "picker.demographics", emptyHouseholdMemberDraft)
  const [saving, setSaving] = useBenefitSaving()
  const { duplicates, checking, error: duplicateError } = usePotentialDuplicates(identity, open && mode === "new")
  const scope = useBenefitScopeArg(open && mode === "existing" && !!submittedSearch)
  const searchRead = benefitApi.useSearchBenefitPeopleQuery(scope !== skipToken ? { ...scope, searchText: submittedSearch } : skipToken)
  const searching = scope !== skipToken && searchRead.isFetching
  const searchError = scope !== skipToken && searchRead.isError ? "Search failed. Please try again." : null
  const hasSearched = !!submittedSearch || !!selected
  const results = submittedSearch
    ? scope !== skipToken && !searching && !searchError ? searchRead.currentData?.matches ?? [] : []
    : selected ? [selected] : []

  const reset = () => {
    setMode("existing")
    setSearchText("")
    setSubmittedSearch("")
    setSelected(null)
    setIdentity(emptyPersonIdentity)
    setDemographics(emptyHouseholdMemberDraft)
  }

  const search = () => {
    const text = searchText.trim()
    if (!text || searching) return
    if (text === submittedSearch && !searchRead.isUninitialized) void searchRead.refetch()
    else setSubmittedSearch(text)
  }

  const alreadyInHousehold = (personId: string) =>
    benefit.personCollection.some((member) => member.id === personId)

  const selectionCurrent = mode !== "existing" || !selected || (!alreadyInHousehold(selected.id) && results.filter(person => person.id === selected.id).length <= 1)
  const add = async () => {
    if (!open || saving || !canEdit || !selectionCurrent) return
    if (mode === "existing" && !selected) {
      toast.error("Choose the person to add to this household.")
      return
    }
    if (mode === "new") {
      const invalid = validatePersonIdentity(identity)
      if (invalid) {
        toast.error(invalid)
        return
      }
      if (duplicates.length > 0) {
        toast.error("Resolve the potential duplicate(s) before creating a new person.")
        return
      }
    }

    if (onStage) {
      onStage(mode === "existing"
        ? { personId: selected!.id, name: formatPersonName(selected!) }
        : { newPerson: newHouseholdMemberPerson(identity), name: formatPersonName(identity) })
      reset()
      onOpenChange(false)
      return
    }

    const body = {
      concurrencyToken,
      ...(mode === "existing"
        ? { personId: selected?.id }
        : { newPerson: newHouseholdMemberPerson(identity) }),
      demographics: householdMemberDemographics(demographics),
    }

    setSaving(true)
    const { saved, reloaded } = await saveBenefitChange(
      benefit.id,
      onUpdated,
      () =>
        api.post<BenefitDetailDto>(
          `/api/benefitapplications/${benefit.id}/household-members`,
          body,
        ),
      "Household member added.",
    )
    setSaving(false)
    if (saved || reloaded) {
      reset()
      onOpenChange(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (saving) return
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent className="demographics-form-dialog" onCloseAutoFocus={event => { if (onReturnFocus) { event.preventDefault(); onReturnFocus() } }}>
        <DialogHeader>
          <DialogTitle>{onStage ? "Select replacement person (not saved yet)" : "Add Household Member"}</DialogTitle>
          <DialogDescription>
            A household member is a real person record. Look for them first — creating a
            second record for someone who already exists splits their history.
          </DialogDescription>
        </DialogHeader>

        <div className="demographics-panel-body" role="region" aria-label="Add member details" tabIndex={0}>
        <Tabs value={mode} onValueChange={(value) => { if (!saving) setMode(value as "existing" | "new") }}>
          <TabsList>
            <TabsTrigger value="existing" disabled={saving}>Find existing person</TabsTrigger>
            <TabsTrigger value="new" disabled={saving}>Create new person</TabsTrigger>
          </TabsList>
          <TabsContent value="existing" className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                aria-label="Search by name or SSN"
                placeholder="Search by name or SSN…"
                value={searchText}
                disabled={saving}
                onChange={(event) => setSearchText(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && search()}
              />
              <Button onClick={search} disabled={searching || !searchText.trim()}>
                {searching ? <IconLoader2 className="size-4 animate-spin" /> : <IconSearch className="size-4" />}
                Search
              </Button>
            </div>
            {selected && <p role="status">Selected person: {formatPersonName(selected)}.{!selectionCurrent && " Already in this household or ambiguous; choose a different person."}</p>}
            <div className="max-h-[240px] overflow-y-auto rounded-md border">
              <Table aria-label="Matching people" className="demographics-search">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>SSN</TableHead>
                    <TableHead>DOB</TableHead>
                    <TableHead><span className="sr-only">Actions</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((match, index) => (
                    <TableRow key={`${match.id}-${index}`} data-state={selected?.id === match.id ? "selected" : undefined}>
                      <TableCell data-label="Name" className="font-medium">
                        {formatPersonName(match)}
                      </TableCell>
                      <TableCell data-label="SSN" className="font-mono text-xs">
                        {canViewIdentifier ? displaySsn(match.socialSecurityNumber) : maskSsn(match.socialSecurityNumber)}
                      </TableCell>
                      <TableCell data-label="Date of birth" className="text-xs text-muted-foreground">
                        {fmtDate(match.dateOfBirth)}
                      </TableCell>
                      <TableCell data-label="Actions" className="text-right">
                        {alreadyInHousehold(match.id) ? (
                          <span className="text-xs text-muted-foreground">Already a member</span>
                        ) : (
                          <Button
                            size="sm"
                            variant={selected?.id === match.id ? "default" : "outline"}
                            onClick={() => setSelected(match)}
                          >
                            {selected?.id === match.id ? "Selected" : "Select"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {searchError && <TableRow><TableCell colSpan={4}><p role="alert" className="text-destructive">{searchError}</p></TableCell></TableRow>}
                  {hasSearched && !searching && !searchError && results.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                        No people found. Try different search terms, or create the person.
                      </TableCell>
                    </TableRow>
                  )}
                  {!hasSearched && (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                        Search for the household member to begin.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          <TabsContent value="new" className="space-y-3">
            <PersonIdentityFields
              value={identity}
              onChange={setIdentity}
              disabled={saving}
              idPrefix="new-member"
            />
            <PotentialDuplicates
              duplicates={duplicates}
              checking={checking}
              error={duplicateError}
              selectLabel="Add the existing person instead of creating a duplicate, or change the details if this is a different person."
              onSelect={(match) => {
                setMode("existing")
                setSelected(match)
                setSubmittedSearch("")
              }}
            />
          </TabsContent>
        </Tabs>

        <Separator />
        {!onStage && <MemberDemographicFields
          draft={demographics}
          onChange={setDemographics}
          disabled={saving}
          isApplicant={false}
          idPrefix="new-member"
        />}

        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false) }} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={add} disabled={saving || !canEdit || !selectionCurrent || (mode === "new" && duplicates.length > 0)}>
            {saving && <IconLoader2 className="size-4 animate-spin" />}
            {onStage ? "Use in correction preview" : "Add Member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function HouseholdMembersSection({
  benefit,
  canEdit,
  onUpdated,
  onHistory,
}: {
  benefit: BenefitDetailDto
  canEdit: boolean
  onUpdated: (updated: BenefitDetailDto) => void
  onHistory?: () => void
}) {
  const [addOpen, setAddOpen, addToken] = useBenefitEdit(benefit.concurrencyToken, "add-member")
  const sorted = demographicsRoster(benefit.personCollection)
  const active = useAppSelector(state => state.benefitEditors.active)
  const captured = active?.kind === "member" && active.benefitId === benefit.id ? active.fields.context?.person : null
  // Editor hosts are keyed independently of roster membership, including a removed member.
  const memberIds = [...new Set([...sorted.map(person => person.id), ...(captured ? [captured.id] : [])])]
  const section = useRef<HTMLDivElement>(null)
  const addButton = useRef<HTMLButtonElement>(null)
  const removal = useMemberRemoval({ benefit, canEdit, onUpdated, section })

  return (
    <Card ref={section} className="demographics-surface">
      <CardHeader>
        <div className="demographics-toolbar">
          <CardTitle><h3 tabIndex={-1} data-member-heading>Household members</h3></CardTitle>
          <div className="demographics-toolbar-actions">
            {onHistory && <Button variant="ghost" size="sm" aria-label="View application history" onClick={onHistory}><IconHistory className="size-4" />Application history</Button>}
            {canEdit && <Button ref={addButton} variant="outline" size="sm" onClick={() => { if (canEdit) setAddOpen(true) }}><IconPlus className="size-4" />Add Member</Button>}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="demographics-roster">
          {sorted.length === 0 && <p className="demographics-empty">No household members were returned for this application.</p>}
          {memberIds.map(id => {
            const matches = sorted.filter(person => person.id === id)
            const person = matches[0] ?? captured!
            return <MemberRecord key={id} benefit={benefit} person={person} showRow={matches.length > 0} canEdit={canEdit} onUpdated={onUpdated} onRemove={removal.open} removing={removal.editing} />
          })}
        </div>
      </CardContent>

      {removal.dialog}
      <AddMemberDialog
        benefit={benefit}
        concurrencyToken={addToken}
        canEdit={canEdit}
        onReturnFocus={() => (addButton.current ?? section.current?.querySelector<HTMLElement>('[data-member-heading]'))?.focus()}
        open={addOpen}
        onOpenChange={setAddOpen}
        onUpdated={onUpdated}
      />
    </Card>
  )
}
